import React from 'react';
import { Linkedin, Facebook, Link2, Check, Mail, Instagram, Rss, Share2, Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../../lib/analytics';

/*
  SocialShare — every published post becomes a promotion engine.
  LinkedIn · X · Facebook · WhatsApp · Email · Instagram caption · copy link · copy RSS.
  Every shared URL carries UTMs (utm_source=share_{network}) so GA4 / Search Console
  attribute the clicks, and every tap is tracked first-party (who shared, when, from where).
*/

function XGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2H21.5l-7.46 8.524L23 22h-6.828l-5.343-6.985L4.69 22H1.43l7.98-9.116L1 2h6.914l4.83 6.39L18.244 2zm-2.4 18h1.836L8.246 4H6.275l9.57 16z" />
    </svg>
  );
}

function WaGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12.04 2a9.9 9.9 0 0 0-8.57 14.86L2 22l5.3-1.39A9.9 9.9 0 1 0 12.04 2zm5.77 14.06c-.24.68-1.4 1.3-1.93 1.35-.52.05-1.01.24-3.4-.71-2.87-1.13-4.7-4.06-4.84-4.25-.14-.19-1.16-1.55-1.16-2.95s.73-2.09 1-2.38c.26-.29.57-.36.76-.36l.55.01c.18.01.41-.07.65.5.24.58.82 2 .89 2.14.07.14.12.31.02.5-.09.19-.14.31-.28.48l-.43.5c-.14.14-.28.29-.12.57.16.29.72 1.19 1.55 1.93 1.07.95 1.97 1.25 2.25 1.39.28.14.44.12.61-.07.16-.19.7-.82.88-1.1.19-.29.38-.24.63-.14.26.1 1.64.77 1.92.91.28.14.47.21.54.33.07.12.07.68-.17 1.35z" />
    </svg>
  );
}

const btn = 'w-9 h-9 rounded-full border border-neutral-200 flex items-center justify-center text-neutral-600 transition-colors';

export default function SocialShare({ url, title, excerpt = '' }) {
  const [done, setDone] = React.useState('');
  const [pinned, setPinned] = React.useState(false);
  React.useEffect(() => {
    const s = (url || '').split('/').filter(Boolean).pop();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins`, { credentials: 'include' })
      .then((r) => r.json()).then((j) => setPinned(!!j.signed_in && (j.slugs || []).includes(s))).catch(() => {});
    // Reading an article counts toward the weekly reading streak (idempotent, anon = noop).
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/streak/touch`, { method: 'POST', credentials: 'include' }).catch(() => {});
  }, [url]);
  const togglePin = async () => {
    log('pin');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins/toggle`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug }),
      });
      const j = await r.json();
      if (!j.signed_in && !j.ok) { toast(`Inicia sesión para guardar artículos en tu biblioteca`, { action: { label: 'Login', onClick: () => { window.location.href = '/login'; } } }); return; }
      setPinned(!!j.pinned);
      toast.success(j.pinned ? 'Guardado en tu biblioteca 📌' : 'Quitado de tu biblioteca');
    } catch { /* noop */ }
  };
  const flash = (k) => { setDone(k); setTimeout(() => setDone(''), 1800); };
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://kitxens.com';
  const slug = (url || '').split('/').filter(Boolean).pop() || 'article';
  const utm = (network) => `${origin}${url}?utm_source=share_${network}&utm_medium=referral_share&utm_campaign=magazine_${slug}`;
  const log = (network) => trackEvent('article_share', { network, article_slug: slug, share_url: `${origin}${url}` });
  const open = (network, href) => { log(network); window.open(href, '_blank', 'noopener,width=640,height=560'); };
  const copy = async (network, text) => {
    log(network);
    try { await navigator.clipboard.writeText(text); flash(network); } catch { /* noop */ }
  };

  const mailHref = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Creo que esto te puede interesar:\n\n${title}\n${utm('email')}\n\n— enviado desde kitxens.com`)}`;
  const igCaption = `${title}\n\n${excerpt || ''}\n\nLee el artículo completo → ${utm('instagram')}\n\n#restaurants #AI #KITXENS`;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="magazine-social-share">
      <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-neutral-400 mr-1">Share</span>
      <button type="button" onClick={togglePin} aria-label="Save to my library" data-testid="share-pin"
        title={pinned ? 'En tu biblioteca' : 'Guardar en tu biblioteca'}
        className={`${btn} ${pinned ? 'text-white bg-[#F77F11] border-[#F77F11]' : 'hover:text-white hover:bg-[#F77F11] hover:border-[#F77F11]'}`}>
        <Bookmark className="w-4 h-4" fill={pinned ? 'currentColor' : 'none'} />
      </button>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(utm('linkedin'))}`}
        onClick={() => log('linkedin')} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" data-testid="share-linkedin"
        className={`${btn} hover:text-white hover:bg-[#0A66C2] hover:border-[#0A66C2]`}>
        <Linkedin className="w-4 h-4" />
      </a>
      <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(utm('x'))}&text=${encodeURIComponent(title)}`}
        onClick={() => log('x')} target="_blank" rel="noopener noreferrer" aria-label="Share on X" data-testid="share-x"
        className={`${btn} hover:text-white hover:bg-black hover:border-black`}>
        <XGlyph className="w-3.5 h-3.5" />
      </a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(utm('facebook'))}`}
        onClick={() => log('facebook')} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" data-testid="share-facebook"
        className={`${btn} hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2]`}>
        <Facebook className="w-4 h-4" />
      </a>
      <button type="button" onClick={() => open('whatsapp', `https://wa.me/?text=${encodeURIComponent(`${title} — ${utm('whatsapp')}`)}`)}
        aria-label="Share on WhatsApp" data-testid="share-whatsapp"
        className={`${btn} hover:text-white hover:bg-[#25D366] hover:border-[#25D366]`}>
        <WaGlyph className="w-4 h-4" />
      </button>
      <button type="button" onClick={() => copy('instagram', igCaption)} aria-label="Copy Instagram caption" data-testid="share-instagram"
        title="Copia el caption listo para Instagram"
        className={`${btn} hover:text-white hover:bg-[#E1306C] hover:border-[#E1306C]`}>
        {done === 'instagram' ? <Check className="w-4 h-4" /> : <Instagram className="w-4 h-4" />}
      </button>
      <a href={mailHref} onClick={() => log('email')} aria-label="Send by email" data-testid="share-email"
        className={`${btn} hover:text-white hover:bg-neutral-800 hover:border-neutral-800`}>
        <Mail className="w-4 h-4" />
      </a>
      <button type="button" onClick={() => copy('copy_link', utm('copy'))} aria-label="Copy link" data-testid="share-copy"
        className={`${btn} hover:text-white hover:bg-[#F77F11] hover:border-[#F77F11]`}>
        {done === 'copy_link' ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
      </button>
      <button type="button" onClick={() => copy('rss', `${origin}/api/rss.xml`)} aria-label="Copy RSS feed" data-testid="share-rss"
        title="Copia la URL del feed RSS"
        className={`${btn} hover:text-white hover:bg-[#f26522] hover:border-[#f26522]`}>
        {done === 'rss' ? <Check className="w-4 h-4" /> : <Rss className="w-4 h-4" />}
      </button>
      {typeof navigator !== 'undefined' && navigator.share && (
        <button type="button" data-testid="share-native" aria-label="Share"
          onClick={() => { log('native'); navigator.share({ title, url: utm('native') }).catch(() => {}); }}
          className={`${btn} hover:text-white hover:bg-neutral-600 hover:border-neutral-600`}>
          <Share2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
